/**
 * Multiplayer client.
 *
 * The browser is never authoritative: it sends commands and renders whatever
 * redacted view the server sends back. It connects to the same origin as the
 * web app (/ws), which the dev server proxies to the internal game server.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Action, GameEvent, PlayerId } from "@kikik/mahjong-core";
import { PROTOCOL_VERSION } from "@kikik/game-protocol";
import type {
  ClientCommand,
  PlayerGameView,
  RoomView,
  ServerEvent,
} from "@kikik/game-protocol";
import { msg } from "@kikik/i18n";
import type { Message } from "@kikik/i18n";
import { describeEvent } from "./eventLog";
import type { LogEntry } from "./eventLog";
import { fromPlayerView } from "./tableModel";
import type { TableModel } from "./tableModel";

export type ConnectionStatus =
  | "idle"
  | "connecting"
  | "reconnecting"
  | "online"
  | "offline"
  | "error";

export interface OnlineGame {
  status: ConnectionStatus;
  room: RoomView | null;
  view: PlayerGameView | null;
  model: TableModel | null;
  log: LogEntry[];
  notice: Message | null;
  displayName: string;
  setDisplayName: (name: string) => void;
  connect: () => void;
  createRoom: () => void;
  joinRoom: (code: string) => void;
  leaveRoom: () => void;
  addBot: () => void;
  startGame: () => void;
  submit: (action: Action) => void;
  clearNotice: () => void;
  /** Increments only when the server announces a new authoritative round. */
  gameSerial: number;
}

const STORAGE_KEY = "kikik.mp.resume";

interface StoredResume {
  roomCode: string;
  sessionId: string;
  resumeToken: string;
}

function readResume(): StoredResume | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<StoredResume> | null;
    if (
      value &&
      typeof value.roomCode === "string" &&
      value.roomCode.length > 0 &&
      typeof value.sessionId === "string" &&
      value.sessionId.length > 0 &&
      typeof value.resumeToken === "string" &&
      value.resumeToken.length > 0
    ) {
      return value as StoredResume;
    }
    globalThis.localStorage?.removeItem(STORAGE_KEY);
    return null;
  } catch {
    return null;
  }
}

function writeResume(value: StoredResume | null): void {
  try {
    if (!value) globalThis.localStorage?.removeItem(STORAGE_KEY);
    else globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Private-mode browsers: resume is a convenience, never a requirement.
  }
}

export interface OnlineGameOptions {
  /** Overridable for tests; defaults to the same origin as the page. */
  url?: string;
  autoConnect?: boolean;
  /** Localised name used until the player types one of their own. */
  defaultName?: string;
}

type BrowserLocation = Pick<Location, "protocol" | "host">;

/** Resolve the public same-origin endpoint, with an explicit development override. */
export function resolveWebSocketUrl(
  location: BrowserLocation | undefined = globalThis.location,
  override: string | undefined = import.meta.env.VITE_KIKIK_WS_URL,
): string {
  const configured = override?.trim();
  if (configured) {
    const url = new URL(configured);
    if (url.protocol !== "ws:" && url.protocol !== "wss:") {
      throw new Error("VITE_KIKIK_WS_URL must use ws:// or wss://");
    }
    return url.toString();
  }
  if (!location) return "ws://127.0.0.1:5173/ws";
  const scheme = location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${location.host}/ws`;
}

const RECONNECT_DELAYS_MS = [500, 1_500, 3_000] as const;
const STABLE_CONNECTION_MS = 30_000;

export function useOnlineGame(options: OnlineGameOptions = {}): OnlineGame {
  const [status, setStatus] = useState<ConnectionStatus>("idle");
  const [room, setRoom] = useState<RoomView | null>(null);
  const [view, setView] = useState<PlayerGameView | null>(null);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [notice, setNotice] = useState<Message | null>(null);
  const [gameSerial, setGameSerial] = useState(0);
  // Empty means "use the localised placeholder", so switching language before
  // typing a name switches the name the server is told, too.
  const [displayName, setDisplayName] = useState("");

  const socketRef = useRef<WebSocket | null>(null);
  const sessionRef = useRef<{ sessionId: string; resumeToken: string } | null>(null);
  const roomCodeRef = useRef<string | null>(null);
  const logId = useRef(0);
  const nameRef = useRef("");
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stableTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);
  const intentionalCloseRef = useRef(false);
  const openSocketRef = useRef<(() => void) | null>(null);
  const automaticDrawsRef = useRef(new Set<string>());
  nameRef.current = displayName.trim() || (options.defaultName ?? "Player");

  const send = useCallback((command: ClientCommand) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setNotice(msg("online.notConnected"));
      return;
    }
    socket.send(JSON.stringify(command));
  }, []);

  const appendEvents = useCallback((events: GameEvent[], you: PlayerId) => {
    const entries = events
      .map((event) => describeEvent(event, you))
      .filter((entry): entry is Omit<LogEntry, "id"> => entry !== null)
      .map((entry) => {
        logId.current += 1;
        return { ...entry, id: logId.current };
      });
    if (entries.length === 0) return;
    setLog((previous) => [...previous, ...entries].slice(-80));
  }, []);

  const sendAutomaticDraw = useCallback((next: PlayerGameView) => {
    if (next.phase !== "awaiting-draw" || next.turn !== next.you) return;
    const draw = next.legalActions.find((action) => action.type === "draw");
    if (!draw) return;

    // actionCount identifies this exact authoritative decision point. Keep the
    // guard across reconnects and repeated snapshots; only a game-started event
    // begins a new namespace. The client still sends an ordinary protocol
    // action and never predicts the resulting state.
    const key = `${next.roomCode}:${next.actionCount}:${next.you}`;
    if (automaticDrawsRef.current.has(key)) return;
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    automaticDrawsRef.current.add(key);
    socket.send(JSON.stringify({ type: "action", action: draw } satisfies ClientCommand));
  }, []);

  const handleEvent = useCallback(
    (event: ServerEvent) => {
      switch (event.type) {
        case "welcome":
          sessionRef.current = {
            sessionId: event.sessionId,
            resumeToken: event.resumeToken,
          };
          setStatus("online");
          if (stableTimerRef.current) clearTimeout(stableTimerRef.current);
          stableTimerRef.current = setTimeout(() => {
            stableTimerRef.current = null;
            retryCountRef.current = 0;
          }, STABLE_CONNECTION_MS);
          if (!event.resumed) writeResume(null);
          break;
        case "room-state":
          setRoom(event.room.seats.length === 0 ? null : event.room);
          roomCodeRef.current = event.room.seats.length === 0 ? null : event.room.code;
          if (event.room.seats.length === 0) {
            setView(null);
            writeResume(null);
          } else if (sessionRef.current) {
            writeResume({ roomCode: event.room.code, ...sessionRef.current });
          }
          break;
        case "game-started":
          automaticDrawsRef.current.clear();
          setRoom(event.room);
          setView(event.view);
          setGameSerial((value) => value + 1);
          roomCodeRef.current = event.room.code;
          setLog([]);
          logId.current = 0;
          sendAutomaticDraw(event.view);
          break;
        case "game-state":
          setView(event.view);
          appendEvents(event.events, event.view.you);
          sendAutomaticDraw(event.view);
          break;
        case "game-ended":
          setView(event.view);
          break;
        case "action-rejected":
          setNotice(
            msg("online.rejected", {
              reason: { $: "msg", key: `error.${event.error.code}` },
            }),
          );
          break;
        case "error":
          if (event.code === "RESUME_FAILED") {
            writeResume(null);
            roomCodeRef.current = null;
            setRoom(null);
            setView(null);
          }
          setNotice(msg("online.serverError", { reason: { $: "msg", key: `error.${event.code}` } }));
          break;
        default:
          break;
      }
    },
    [appendEvents, sendAutomaticDraw],
  );

  const handleEventRef = useRef(handleEvent);
  handleEventRef.current = handleEvent;

  const openSocket = useCallback(() => {
    if (socketRef.current) return;
    setStatus(retryCountRef.current === 0 ? "connecting" : "reconnecting");
    let socket: WebSocket;
    try {
      socket = new WebSocket(resolveWebSocketUrl(globalThis.location, options.url));
    } catch {
      setStatus("error");
      setNotice(msg("online.connectFailed"));
      return;
    }
    socketRef.current = socket;

    socket.onopen = () => {
      const resume = readResume();
      const hello: ClientCommand = {
        type: "hello",
        protocolVersion: PROTOCOL_VERSION,
        displayName: nameRef.current,
      };
      if (resume) hello.resume = resume;
      socket.send(JSON.stringify(hello));
    };
    socket.onmessage = (message) => {
      try {
        handleEventRef.current(JSON.parse(String(message.data)) as ServerEvent);
      } catch {
        setNotice(msg("online.badMessage"));
      }
    };
    socket.onclose = () => {
      socketRef.current = null;
      if (stableTimerRef.current) clearTimeout(stableTimerRef.current);
      stableTimerRef.current = null;
      if (intentionalCloseRef.current || !mountedRef.current) return;
      const delay = RECONNECT_DELAYS_MS[retryCountRef.current];
      if (delay === undefined) {
        setStatus("error");
        setNotice(msg("online.reconnectFailed"));
        return;
      }
      retryCountRef.current += 1;
      setStatus("reconnecting");
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        openSocketRef.current?.();
      }, delay);
    };
    socket.onerror = () => {
      setNotice(msg("online.connectFailed"));
    };
  }, [options.url]);
  openSocketRef.current = openSocket;

  const connect = useCallback(() => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    if (stableTimerRef.current) clearTimeout(stableTimerRef.current);
    retryTimerRef.current = null;
    stableTimerRef.current = null;
    retryCountRef.current = 0;
    intentionalCloseRef.current = false;
    openSocketRef.current?.();
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (options.autoConnect !== false) connect();
    return () => {
      mountedRef.current = false;
      intentionalCloseRef.current = true;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (stableTimerRef.current) clearTimeout(stableTimerRef.current);
      retryTimerRef.current = null;
      stableTimerRef.current = null;
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [connect, options.autoConnect]);

  const model = useMemo(
    () =>
      view
        ? fromPlayerView(view, (playerId) => room?.seats[playerId]?.displayName ?? null)
        : null,
    [view, room],
  );

  return {
    status,
    room,
    view,
    model,
    log,
    notice,
    displayName,
    setDisplayName,
    connect,
    createRoom: useCallback(
      () => send({ type: "create-room", displayName: nameRef.current }),
      [send],
    ),
    joinRoom: useCallback(
      (code: string) =>
        send({ type: "join-room", roomCode: code.trim().toUpperCase(), displayName: nameRef.current }),
      [send],
    ),
    leaveRoom: useCallback(() => {
      writeResume(null);
      send({ type: "leave-room" });
    }, [send]),
    addBot: useCallback(() => send({ type: "add-bot" }), [send]),
    startGame: useCallback(() => send({ type: "start-game" }), [send]),
    submit: useCallback((action: Action) => send({ type: "action", action }), [send]),
    clearNotice: useCallback(() => setNotice(null), []),
    gameSerial,
  };
}
